

import PropTypes from 'prop-types';
import { makeStyles } from 'tss-react/mui';
import { darken } from '@mui/material/styles';
import { Fab } from '@mui/material';
import clsx from 'clsx';

// relative imports
import {
  enableWhiteLabelStyling,
  setFontSize,
} from '../../utilities/styleUtilities';

const customColors = (theme, label, ignoreWhiteLabel) => {
  if (theme.whiteLabel.Button.color && !ignoreWhiteLabel) {
    return theme.whiteLabel.Button.color;
  }

  return {
    success: theme.palette.success.main,
    successDark: theme.palette.success.dark,
    successLight: theme.palette.success.light,
    cancel: theme.palette.error.main,
    cancelDark: theme.palette.error.dark,
    cancelLight: theme.palette.error.light,
    primary: theme.palette.primary.main,
    primaryDark: theme.palette.primary.dark,
    primaryLight: theme.palette.primary.light,
    secondary: theme.palette.secondary.main,
    secondaryDark: theme.palette.secondary.dark,
    secondaryLight: theme.palette.secondary.light,
  }[label];
};

const muiSizes = ['small', 'medium', 'large'];

const muiColors = ['default', 'inherit', 'primary', 'secondary', 'whiteLabel'];

const useStyles = makeStyles()(
  (
    theme,
    {
      ignoreWhiteLabel,
      size,
      fontSize,
      rightTextPad,
      color,
      margin,
      width,
      height,
      textTransformOff,
    },
  ) => ({
    fabLabel: {
      color: enableWhiteLabelStyling({
        ignoreWhiteLabel,
        whiteLabelStyle: theme.whiteLabel.HeaderText.color,
        defaultStyle: theme.palette.common.white,
      }),
      '&:hover': {
        color: enableWhiteLabelStyling({
          ignoreWhiteLabel,
          whiteLabelStyle: theme.whiteLabel.HeaderText.color,
          defaultStyle: theme.palette.common.white,
        }),
      },
      fontSize: setFontSize({ theme, size, fontSize }),
      paddingRight: rightTextPad ? `${rightTextPad}px` : 0,
    },
    fabBackground: {
      margin: `${margin.top}px ${margin.right}px ${margin.bottom}px ${margin.left}px`,
      backgroundColor: color
        ? customColors(theme, color, ignoreWhiteLabel)
        : 'default',
      '&:hover': {
        backgroundColor: color
          ? darken(customColors(theme, color, ignoreWhiteLabel), 0.2)
          : 'inherit',
      },
      width: width || 'inherit',
      height: height || 'inherit',
      border: enableWhiteLabelStyling({
        ignoreWhiteLabel,
        whiteLabelStyle: `${
          theme.whiteLabel.Button.borderWidth || '1px'
        } solid ${theme.whiteLabel.Button.borderColor}`,
        defaultStyle: null,
      }),
      textTransform: textTransformOff ? 'none' : null,
    },
    smallerLabel: {
      fontSize: theme.typography.pxToRem(11),
    },
    smallerBackground: {
      height: 32,
      padding: theme.spacing(0.6, 0.8),
    },
  }),
);

const LedgibleFab = ({
  children,
  color,
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  buttonClasses,
  textTransformOff,
  rightTextPad,
  height,
  width,
  fontSize,
  id,
  ignoreWhiteLabel,
  ...rest
}) => {
  const margin = {
    top: marginTop || 0,
    right: marginRight || 0,
    bottom: marginBottom || 0,
    left: marginLeft || 0,
  };

  const { classes } = useStyles({
    color,
    size: rest.size,
    rightTextPad,
    height,
    width,
    margin,
    textTransformOff,
    fontSize,
    ignoreWhiteLabel,
  });

  // CONSIDER => additional custom sizes = smaller, smallest, larger, largest
  const labelClassname = clsx({
    [classes.fabLabel]: true,
    [classes.smallerLabel]: rest.size === 'smaller',
  });

  const buttonClassname = clsx({
    [classes.fabBackground]: true,
    [classes.smallerBackground]: rest.size === 'smaller',
  });

  return (
    <Fab
      classes={{
        label: labelClassname,
        root: buttonClassname,
      }}
      color={!muiColors.includes(color) ? 'default' : color}
      size={!muiSizes.includes(rest.size) ? 'large' : rest.size}
      id={id}
      data-cy={id}
      {...rest}
    >
      {children}
    </Fab>
  );
};

export default LedgibleFab;

LedgibleFab.propTypes = {
  children: PropTypes.node,
  color: PropTypes.string,
  marginTop: PropTypes.number,
  marginRight: PropTypes.number,
  marginBottom: PropTypes.number,
  marginLeft: PropTypes.number,
  buttonClasses: PropTypes.shape({}),
  textTransformOff: PropTypes.bool,
  rightTextPad: PropTypes.number,
  height: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  width: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  fontSize: PropTypes.number,
  ignoreWhiteLabel: PropTypes.bool,
};

LedgibleFab.defaultProps = {
  children: null,
  color: undefined,
  marginTop: 0,
  marginRight: 0,
  marginBottom: 0,
  marginLeft: 0,
  buttonClasses: {},
  textTransformOff: false,
  rightTextPad: 0,
  height: 'default',
  width: 'default',
  title: '',
  fontSize: undefined,
  ignoreWhiteLabel: false,
};
